import { useState } from 'react';
import { FileKey, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui';
import type { Certificado } from '@/api/client';
import { maskCpfPrivate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { CertificateStatusBadge, certValidade, type CertValidadeTone } from './CertificateStatusBadge';
import { CertificateApelidoDialog } from './CertificateApelidoDialog';

/** Curto: "10/2027" — a data inteira não cabe na linha de detalhes. */
function formatMesAno(isoDate: string): string {
  try {
    return format(new Date(isoDate), 'MM/yyyy', { locale: ptBR });
  } catch {
    return isoDate;
  }
}

/** Cor da faixa lateral: a validade de relance (inativo fica neutro). */
const SPINE: Record<CertValidadeTone, string> = {
  'sem-dados': 'bg-muted-foreground/40',
  expirado: 'bg-red-500',
  expirando: 'bg-yellow-500',
  valido: 'bg-emerald-500',
};

interface CertificateListProps {
  certificates: Certificado[];
  loading: boolean;
  onToggle: (cert: Certificado) => void;
  onRefresh: () => void;
  /** Apelido salvo (ou removido, `null`) pelo diálogo de edição. */
  onApelidoChange?: (numeroSerie: string, apelido: string | null) => void;
}

/**
 * Lista "Meus certificados" em CARTÕES — numa tabela as seis colunas não
 * cabiam na tela estreita. Cada cartão: faixa lateral com a validade, apelido
 * (ou titular) em destaque com o lápis do apelido, CPF/validade/emissor numa
 * linha de detalhes, e à direita o selo de validade e o switch de ativo.
 *
 * Desativar pede confirmação num diálogo (ativar é direto).
 */
export function CertificateList({
  certificates,
  loading,
  onToggle,
  onApelidoChange,
}: CertificateListProps) {
  const [toggleTarget, setToggleTarget] = useState<Certificado | null>(null);
  const [apelidoTarget, setApelidoTarget] = useState<Certificado | null>(null);

  const handleToggle = (cert: Certificado) => {
    if (cert.ativo) {
      setToggleTarget(cert);
    } else {
      onToggle(cert);
    }
  };

  const confirmDeactivation = () => {
    if (!toggleTarget) return;
    onToggle(toggleTarget);
    setToggleTarget(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size={32} label="Carregando certificados" />
      </div>
    );
  }

  if (certificates.length === 0) {
    return (
      <EmptyState
        icon={<FileKey className="h-7 w-7" />}
        title="Nenhum certificado cadastrado"
        description="Cadastre seu certificado digital A1 para começar a assinar petições."
      />
    );
  }

  return (
    <>
      <ul className="space-y-2.5" aria-label="Meus certificados">
        {certificates.map((cert) => {
          const titulo = cert.apelido || cert.nome || cert.numero_serie;
          const cpf = maskCpfPrivate(cert.cpf_certificado);
          const { tone } = certValidade(cert);
          return (
            <li
              key={cert.numero_serie}
              data-certificado-card
              className="relative flex flex-wrap items-center gap-3 overflow-hidden rounded-lg border bg-card p-4 pl-5"
            >
              <span
                aria-hidden="true"
                className={cn('absolute inset-y-0 left-0 w-1', cert.ativo ? SPINE[tone] : 'bg-muted-foreground/40')}
              />
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary',
                  !cert.ativo && 'opacity-60',
                )}
              >
                <FileKey className="h-4 w-4" />
              </span>

              <div className="min-w-0 flex-1 basis-48">
                <div className="flex items-center gap-1">
                  <p className="min-w-0 text-sm font-semibold [overflow-wrap:anywhere]">{titulo}</p>
                  {onApelidoChange && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 shrink-0 text-muted-foreground sm:h-8 sm:w-8"
                          onClick={() => setApelidoTarget(cert)}
                          aria-label={`${cert.apelido ? 'Editar' : 'Dar'} apelido ao certificado de ${cert.nome || cert.numero_serie}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{cert.apelido ? 'Editar apelido' : 'Dar um apelido'}</TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-muted-foreground">
                  {cert.apelido && cert.nome && <span className="[overflow-wrap:anywhere]">{cert.nome}</span>}
                  {cpf && cpf !== '-' && <span className="tabular-nums">{cpf}</span>}
                  {cert.validade_fim && (
                    <span className="tabular-nums">validade {formatMesAno(cert.validade_fim)}</span>
                  )}
                  {cert.emissor && <span className="[overflow-wrap:anywhere]">{cert.emissor}</span>}
                </div>
              </div>

              <div className="ml-auto flex shrink-0 items-center gap-3">
                <CertificateStatusBadge cert={cert} />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground" aria-hidden="true">
                    {cert.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                  <Switch
                    checked={cert.ativo}
                    onCheckedChange={() => handleToggle(cert)}
                    aria-label={
                      cert.ativo
                        ? `Desativar certificado ${cert.numero_serie}`
                        : `Ativar certificado ${cert.numero_serie}`
                    }
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Dialog de confirmação de desativação */}
      <Dialog open={!!toggleTarget} onOpenChange={(open: boolean) => !open && setToggleTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desativar certificado</DialogTitle>
            <DialogDescription>
              Deseja realmente desativar o certificado de{' '}
              <span className="font-semibold">
                &quot;{toggleTarget?.apelido || toggleTarget?.nome || toggleTarget?.numero_serie}&quot;
              </span>
              ? Ele não poderá ser usado para assinar petições enquanto estiver inativo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToggleTarget(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDeactivation}>
              Desativar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {onApelidoChange && (
        <CertificateApelidoDialog
          cert={apelidoTarget}
          onClose={() => setApelidoTarget(null)}
          onSaved={onApelidoChange}
        />
      )}
    </>
  );
}
